import { BlogPostInput } from './blogService';

// This interface represents the options we can pass to the AI for content generation
export interface BlogAIOptions {
  topic?: string;
  keywords?: string[];
  tone?: 'professional' | 'casual' | 'enthusiastic' | 'informative';
  length?: 'short' | 'medium' | 'long';
  purpose?: 'educate' | 'promote' | 'entertain' | 'inform';
  audienceLevel?: 'beginner' | 'intermediate' | 'advanced';
  includeSEO?: boolean;
  includeCTA?: boolean; // Call to action
  includeAppBenefits?: boolean;
}

// Helper function to construct the prompt for the AI
const constructPrompt = (options: BlogAIOptions): string => {
  // Default prompt parts
  const baseParts = [
    "Write a blog post",
    options.topic ? `about ${options.topic}` : "",
  ];

  // Add tone guidance
  if (options.tone) {
    baseParts.push(`in a ${options.tone} tone`);
  }

  // Add purpose
  if (options.purpose) {
    const purposeMap = {
      educate: "that educates readers",
      promote: "that promotes the product",
      entertain: "that entertains readers",
      inform: "that informs readers about the latest developments"
    };
    baseParts.push(purposeMap[options.purpose]);
  }

  // Add length guidance
  if (options.length) {
    const lengthMap = {
      short: "Keep it concise and around 300-500 words.",
      medium: "Aim for approximately 800-1200 words.",
      long: "Create a comprehensive article of 1500-2000 words."
    };
    baseParts.push(lengthMap[options.length]);
  }

  // Add audience level
  if (options.audienceLevel) {
    const levelMap = {
      beginner: "for beginners who are just getting started",
      intermediate: "for those with some existing knowledge",
      advanced: "for advanced users looking for in-depth information"
    };
    baseParts.push(levelMap[options.audienceLevel]);
  }

  // Add keywords if provided
  if (options.keywords && options.keywords.length > 0) {
    baseParts.push(`Include the following keywords naturally in the text: ${options.keywords.join(', ')}.`);
  }

  // Add SEO guidance if requested
  if (options.includeSEO) {
    baseParts.push("Optimize the content for search engines by using proper headings (H1, H2, H3), including the main keyword in the first paragraph, and maintaining a good keyword density.");
  }

  // Add call to action if requested
  if (options.includeCTA) {
    baseParts.push("Include a compelling call to action at the end of the post.");
  }

  // Add app benefits mention if requested
  if (options.includeAppBenefits) {
    baseParts.push("Throughout the post, naturally mention the benefits of our PLAB2 simulator app where relevant, such as realistic patient scenarios, interactive practice, and comprehensive feedback.");
  }

  // Structure instruction for the AI
  baseParts.push(`
  
Format the post with the following structure:
1. A catchy title
2. A brief summary/introduction (2-3 sentences)
3. The main content with proper headings and subheadings
4. A conclusion

Return the post in JSON format with these fields:
{
  "title": "Post Title Here",
  "summary": "Brief summary here...",
  "content": "Full post content with HTML formatting here..."
}`);

  // Join all parts into a single prompt
  return baseParts.filter(part => part.length > 0).join(" ");
};

// Helper function to get the API key
const getApiKey = (): string => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error("Missing Gemini API key. Please add REACT_APP_GEMINI_API_KEY to your environment variables.");
  }
  return apiKey;
};

// This function calls the Gemini API to generate blog content
export const generateBlogPost = async (options: BlogAIOptions): Promise<Partial<BlogPostInput> | null> => {
  try {
    const apiKey = getApiKey();
    
    const prompt = constructPrompt(options);
    
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro/generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{ 
          parts: [{ text: prompt }] 
        }],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 8192
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`AI service error: ${errorData?.error?.message || response.statusText}`);
    }

    const data = await response.json();
    
    // Extract the JSON response from the text
    const generatedText = data.candidates[0].content.parts[0].text;
    
    // Find the JSON object in the response
    const jsonMatch = generatedText.match(/\{[\s\S]*\}/);
    
    if (jsonMatch) {
      try {
        const result = JSON.parse(jsonMatch[0]);
        return {
          title: result.title,
          summary: result.summary,
          content: result.content
        };
      } catch (parseError) {
        console.error('Error parsing AI response as JSON:', parseError);
        // As a fallback, try to extract the parts manually
        const titleMatch = generatedText.match(/title["\s:]+([^\n"]+)/i);
        const summaryMatch = generatedText.match(/summary["\s:]+([^\n"]+)/i);
        const contentStart = generatedText.indexOf('content');
        
        if (titleMatch && summaryMatch && contentStart > -1) {
          return {
            title: titleMatch[1].trim(),
            summary: summaryMatch[1].trim(),
            content: generatedText.substring(contentStart).replace(/content["\s:]+/i, '').trim()
          };
        }
      }
    }
    
    // If we couldn't extract structured content, return the raw text
    return {
      title: "Generated Blog Post",
      summary: "AI-generated content",
      content: generatedText
    };
  } catch (error) {
    console.error('Error generating blog post with AI:', error);
    return null;
  }
};

// Function to enhance or improve an existing blog post
export const enhanceBlogPost = async (
  existingContent: string, 
  instructions: string
): Promise<string | null> => {
  try {
    const apiKey = getApiKey();
    
    const prompt = `
      Improve the following blog post according to these instructions: ${instructions}
      
      EXISTING CONTENT:
      ${existingContent}
      
      Please return ONLY the improved content. Maintain HTML formatting.
    `;
    
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro/generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{ 
          parts: [{ text: prompt }] 
        }],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 8192
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`AI service error: ${errorData?.error?.message || response.statusText}`);
    }

    const data = await response.json();
    const improvedContent = data.candidates[0].content.parts[0].text;
    
    return improvedContent;
  } catch (error) {
    console.error('Error enhancing blog post with AI:', error);
    return null;
  }
};

// Function to generate an SEO-friendly title and meta description
export const generateSEO = async (
  content: string
): Promise<{title: string, description: string} | null> => {
  try {
    const apiKey = getApiKey();
    
    const prompt = `
      Based on the following blog post content, generate:
      1. An SEO-friendly title (max 60 characters)
      2. A compelling meta description (max 155 characters)
      
      Make sure both are keyword-rich, engaging, and accurately represent the content.
      
      CONTENT:
      ${content.substring(0, 2000)}... (truncated)
      
      Return ONLY a JSON object in this exact format:
      {
        "title": "SEO Title Here",
        "description": "Meta description here..."
      }
    `;
    
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro/generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{ 
          parts: [{ text: prompt }] 
        }],
        generationConfig: {
          temperature: 0.4,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 1024
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`AI service error: ${errorData?.error?.message || response.statusText}`);
    }

    const data = await response.json();
    const seoText = data.candidates[0].content.parts[0].text;
    
    // Extract JSON
    const jsonMatch = seoText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const result = JSON.parse(jsonMatch[0]);
      return {
        title: result.title,
        description: result.description
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error generating SEO with AI:', error);
    return null;
  }
};

// Function to suggest improvements for a blog post
export const suggestImprovements = async (
  content: string
): Promise<string[] | null> => {
  try {
    const apiKey = getApiKey();
    
    const prompt = `
      Analyze the following blog post and suggest 5 specific improvements that would make it more engaging, 
      SEO-friendly, and valuable to readers. Consider aspects like readability, structure, content depth, and engagement.
      
      CONTENT:
      ${content.substring(0, 3000)}... (truncated)
      
      Return ONLY a JSON array of 5 suggestion strings in this format:
      ["Suggestion 1", "Suggestion 2", "Suggestion 3", "Suggestion 4", "Suggestion 5"]
    `;
    
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro/generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{ 
          parts: [{ text: prompt }] 
        }],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 2048
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`AI service error: ${errorData?.error?.message || response.statusText}`);
    }

    const data = await response.json();
    const suggestionsText = data.candidates[0].content.parts[0].text;
    
    // Extract JSON array
    const jsonMatch = suggestionsText.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[0]);
    }
    
    return null;
  } catch (error) {
    console.error('Error generating improvement suggestions with AI:', error);
    return null;
  }
}; 